import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import api from "../../../../services/Api";
import { updateAiDrink } from "../../../../services/Admin/AiDrinksApi";

const initialFormData = {
  drink_name: "",
  description: "",
  status: "1",
  temperature: "",
  category_id: "",
  tastes: "",
  price: "",
  prepare_time: "",
  image: null,
};

const getImageUrl = (image) => {
  if (!image) return null;

  const imageValue = String(image);
  if (/^(https?:|data:|blob:)/i.test(imageValue)) return imageValue;

  const baseURL = api.defaults.baseURL || "";
  return `${baseURL.replace(/\/$/, "")}/${imageValue.replace(/^\//, "")}`;
};

const UpdateDrinkDataDialog = ({ open, onClose, drink, onUpdated }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (open && drink) {
      setFormData({
        drink_name: drink.drink_name || drink.name || drink.name_en || "",
        description: drink.description || drink.description_en || "",
        status: drink.status?.toString() || "1",
        temperature: drink.temperature || "",
        category_id: drink.category_id || drink.category?.id || "",
        tastes: drink.tastes || drink.tasts || "",
        price: drink.price || "",
        prepare_time: drink.prepare_time || "",
        image: null,
      });
      setImagePreview(getImageUrl(drink.image));
      setError("");
      setSuccessMessage("");
    }
  }, [open, drink]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setFormData((prev) => ({
      ...prev,
      image: file,
    }));

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const validateForm = () => {
    if (!formData.drink_name.trim()) {
      setError("اسم المشروب مطلوب");
      return false;
    }

    if (!formData.price) {
      setError("السعر مطلوب");
      return false;
    }

    if (!formData.prepare_time) {
      setError("وقت التحضير مطلوب");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    setError("");
    setSuccessMessage("");

    if (!validateForm()) return;

    setLoading(true);

    try {
      const cleanName = String(formData.drink_name || "").trim();
      const cleanDescription = String(formData.description || "").trim();

      await updateAiDrink(drink.id, {
        drink_name: cleanName,
        drink_name_en: cleanName,
        name: cleanName,
        name_en: cleanName,
        description: cleanDescription,
        description_en: cleanDescription,
        status: formData.status,
        temperature: formData.temperature,
        category_id: formData.category_id,
        tasts: formData.tastes,
        price: formData.price,
        prepare_time: formData.prepare_time,
        image: formData.image,
      });

      setSuccessMessage("تم تحديث بيانات المشروب بنجاح");
      setTimeout(() => {
        onUpdated?.();
        onClose();
      }, 800);
    } catch (err) {
      console.error("خطأ في تحديث المشروب:", err);
      setError(err.message || "حدث خطأ في تحديث بيانات المشروب");
    } finally {
      setLoading(false);
    }
  };

  if (!drink) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: "primary.main", color: "white", fontWeight: "bold", textAlign: "right" }}>
        تعديل بيانات المشروب: {drink.drink_name}
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} dir="rtl">
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: "bold", color: "primary.main", mb: 1 }}>
                المعلومات الأساسية
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="اسم المشروب الجديد"
                name="drink_name"
                value={formData.drink_name}
                onChange={handleInputChange}
                placeholder="مثال: قهوة الضيف"
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="درجة الحرارة"
                name="temperature"
                value={formData.temperature}
                onChange={handleInputChange}
                placeholder="مثال: ساخن جدا"
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="الطعم"
                name="tastes"
                value={formData.tastes}
                onChange={handleInputChange}
                placeholder="مثال: مر، حلو، قوي"
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="الوصف الجديد"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={3}
                placeholder="أضف وصف المشروب"
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: "bold", color: "primary.main", mb: 1 }}>
                السعر والتحضير
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="السعر"
                name="price"
                type="number"
                value={formData.price}
                onChange={handleInputChange}
                inputProps={{ step: "0.01", min: "0" }}
                placeholder="0.00"
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="وقت التحضير (دقيقة)"
                name="prepare_time"
                type="number"
                value={formData.prepare_time}
                onChange={handleInputChange}
                inputProps={{ min: "0" }}
                placeholder="5"
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>الحالة</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  label="الحالة"
                >
                  <MenuItem value="1">نشط</MenuItem>
                  <MenuItem value="0">غير نشط</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: "bold", color: "primary.main", mb: 1 }}>
                الصورة
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Button variant="outlined" component="label" fullWidth sx={{ textAlign: "center", py: 2 }}>
                اختر صورة
                <input hidden accept="image/*" type="file" onChange={handleImageChange} />
              </Button>
            </Grid>

            {imagePreview && (
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, textAlign: "center" }}>
                  <img
                    src={imagePreview}
                    alt="معاينة"
                    style={{ maxHeight: 180, maxWidth: "100%", borderRadius: 8 }}
                  />
                  <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
                    معاينة الصورة
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>


      <DialogActions sx={{ p: 2, gap: 1, justifyContent: "flex-start" }}>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="success"
          startIcon={<SaveIcon />}
          disabled={loading}
        >
          {loading ? "جاري التحديث..." : "حفظ التغييرات"}
        </Button>
        <Button onClick={onClose} variant="outlined" startIcon={<CancelIcon />} disabled={loading}>
          إلغاء
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateDrinkDataDialog;
